import React, { FC, useMemo, useState } from 'react';

import cn from 'classnames';
import dayjs from 'dayjs'
import dynamic from 'next/dynamic';
import Image from 'next/legacy/image';
import { useRouter } from 'next/router';

import { ImgShadow } from '@components/ImgShadow';
import { AIRDROP_PATHS, COMMUNITY_PATHS } from '@constants/paths';
import { useClient, useIsomorphicLayoutEffect } from '@lib/hooks';
import pubsub from '@lib/scripts/pubsub'
import { helper, I18n } from '@utils/index';
import { BCD } from '@widget/airdrop/countdown';

import { airdropReceiveStatus, airdropStatus, defaultNickname } from '../constants';
import { usePrecision } from '../hooks/usePrecision';

import styles from './index.module.less';
const AirdropUserinfo = dynamic(
  () => import('@components/UserInfo').then((module) => module.AirdropUserinfo),
  { ssr: false }
);
const defaultAvatar = require('@images/defaultAvatar.webp');
export interface AirCardItem {
  /** 空投类型 */
  assignType: string;
  /** 用户头像 */
  avatar: string;
  /** 空投数值 */
  amount: string;
  /** 空投价值（货币类型） */
  currency: any;
  /** 空投状态 （待领取， 已领完，已过期） */
  status: number;
  /** 空投封面 */
  cover: string;
  /** 空投id */
  id: string;
  /** 发空投用户名 */
  releaseNickName?: string;
  /** 用户名 */
  nickName?: string;
  /** 空投收到时间 */
  receiveTime?: number;
  /** 空投还有多久过期 ms */
  remainTime?: number;
  /** 空投过期时间 */
  expiredTime?: number;
  /** 收到空投Id */
  receiveId?: string;
}
interface AirCardProps extends AirCardItem {
  turnOnAirDrop?: (item: AirCardItem) => void; // 领取空投
  className?: string;
}
const dropCover = {
  FLOWER: {
    url: require('@images/airdrop/rose.webp'),
    width: 85,
    height: 188
  },
  CAR: {
    url: require('@images/airdrop/car.webp'),
    width: 236,
    height: 116
  },
  TOILET: {
    url: require('@images/airdrop/toilet.webp'),
    width: 135,
    height: 179
  },
  ROCKET: {
    url: require('@images/airdrop/rocket.webp'),
    width: 110,
    height: 164
  },
  QUESTION: {
    url: require('@images/airdrop/question.webp'),
    width: 148,
    height: 127
  }
};
const getExpiredDom = (num:number) => {
  const arr = [];
  for (let i = 0; i < num; i++) {
    arr.push(I18n.t('FIA4Fm8b'));
  }
  return arr;
};
export const AirCard: FC<AirCardProps> = (props) => {
  const {
    assignType,
    remainTime,
    expiredTime,
    avatar,
    releaseNickName,
    nickName,
    currency,
    status,
    cover,
    id,
    amount,
    receiveId,
    className,
    turnOnAirDrop
  } = props;
  const isPc = useClient();
  const assignTypeMap = {
    FORTUNE: I18n.t('v2urRDkV'),
    GENERAL: I18n.t('VEFGCZOJ')
  };
  // 判断是否是已领取的空投，如果已领取展示封面图片，如果未领取展示默认封面
  const coverStr = status === airdropReceiveStatus.noActive ? 'QUESTION' : cover;
  const router = useRouter();
  const precision = usePrecision({ coinCode: currency });
  const [action, setAction] = useState('open-airdrop');
  const onclick = (e) => {
    e.preventDefault();
    if (![airdropReceiveStatus.expired, airdropReceiveStatus.noActive].includes(status) || (
      !receiveId && status === airdropReceiveStatus.expired // 空投历史 - 发出定投 过期跳转详情
    )) {
      router.push({
        pathname: AIRDROP_PATHS.DETAIL + id,
        query: receiveId ? `receiveId=${receiveId}` : ''
      });
    } else {
      // 主页未激活 & 过期 展示弹窗 —— 其余状态跳转详情
      turnOnAirDrop(props);
    }
  };
  const turnOn = (e) => {
    e.stopPropagation();
    turnOnAirDrop(props);
  };
  const getLinkHref = (e) => {
    e.stopPropagation();
    receiveId && router.push(`${COMMUNITY_PATHS.USER_HOME}${receiveId}`);
  };
  // 倒计时结束回调
  const bcdCallback = () => {
    pubsub.publish('UPDATA_CLAIM_HISTORY')
  }
  const getExpiredTime = useMemo(() => {
    const time = remainTime
      ? dayjs().unix() + remainTime / 1000
      : expiredTime / 1000;
    if (!(time > dayjs().unix())) {
      return 0;
    }
    return time;
  }, [remainTime, expiredTime]);
  const getExpired = useMemo(() => {
    if ((status === airdropReceiveStatus.noActive && !getExpiredTime) || status === airdropReceiveStatus.expired) {
      return <div className={styles.bg}>
      <div className={cn(styles.expired, styles.right)}>
        <div className={styles.expiredContent}>
          {getExpiredDom(4).map((item, index) => <span key={index}>{item}</span>)}
        </div>
      </div>
      <div className={styles.expired}>
        <div className={styles.expiredContent}>
          {getExpiredDom(4).map((item, index) => <span key={index}>{item}</span>)}
        </div>
      </div>
    </div>
    }
  }, [status, getExpiredTime]);
  const getNoActive = useMemo(() => {
    if (status !== airdropReceiveStatus.noActive && status !== airdropStatus.drewing) {
      return <></>;
    } else if (status === airdropReceiveStatus.noActive) {
      return <>
        <BCD endTime={getExpiredTime} className={styles.deadline} cb={bcdCallback} />
        <span className={styles.turnOn} onClick={turnOn} data-buried="open-airdrop">{I18n.t('liYVFFzG')}</span>
      </>;
    } else if (status === airdropStatus.drewing) {
      return <BCD endTime={getExpiredTime} className={styles.deadline} cb={bcdCallback} />
    }
  }, [status]);
  const getAmount = useMemo(() => {
    const currency = helper.formatFixNumString(amount, precision);
    if (isPc) {
      return currency.toString().length > 15 ? currency.toString().slice(0, 15) : currency
    } else {
      return currency.toString().length > 8 ? currency.toString().slice(0, 8) : currency
    }
  }, [amount, isPc])
  useIsomorphicLayoutEffect(() => {
    if (![airdropReceiveStatus.expired, airdropReceiveStatus.noActive].includes(status) || (
      !receiveId && status === airdropReceiveStatus.expired // 空投历史 - 发出定投 过期跳转详情
    )) {
      setAction('check-detail');
    } else {
      setAction('open-airdrop');
    }
  }, [status])
  return (
    <div className={styles.container}>
      <div
        className={cn(className, styles.wrapper, {
          [styles.noExpired]: status !== airdropReceiveStatus.expired
        })}
        onClick={onclick}
        data-buried={action}
      >
        {getExpired}
        <div className={styles.content}>
          <ImgShadow
            width={dropCover[coverStr]?.width}
            height={dropCover[coverStr]?.height}
            src={dropCover[coverStr]?.url}
            className={styles.img}
          />
          <span className={cn(styles.type, 'DINPro')}>
            {assignTypeMap[assignType]}
          </span>
          {!!getExpiredTime && getNoActive}
        </div>
        <div className={styles.message}>
          <div onClick={getLinkHref} onTouchStart={getLinkHref}>

            <AirdropUserinfo
              className={styles.userInfo}
              ImgTag={Image}
              imgTagProps={{ width: '44', height: '44', layout: 'fixed' }}
              src={avatar || defaultAvatar}
              desc={I18n.t('Q14l5ZA3')}
              nickname={releaseNickName || nickName || defaultNickname.nickName}
            />
          </div>
          <div className={styles.value}>
            <span>{I18n.t('LE73Mfxm')}</span>
            <span>
              {/* {helper.formatFixNumString(amount, precision)}  */}
              {getAmount} {currency}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
